// Copyright ©️ 2026 eVolve MEP, LLC

import { Group, Loader, Stack, Switch, Text } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import type { AutoDimensioningStatus } from 'components/Autodesk/extensions/DimensioningExtension/createDimensioningStore';
import {
  useAutodeskExtensionStoreActions,
  useAutodeskExtensionStoreSnapshot,
} from 'components/Autodesk/useAutodeskExtensionStore';

const statusMessages: Record<Exclude<AutoDimensioningStatus, 'supported'>, string> = {
  unsupported:
    'No conduit rack found in the selected spools. Automatic dimensions need two or more conduits sharing a strut.',
  noSelection: 'Isolate a single spool to show automatic dimensions.',
  measuring: 'Attempting to find conduit rack dimensions',
};

const AutoDimensionStatusSummary = () => {
  const { autoDimensioningStatus } = useAutodeskExtensionStoreSnapshot('dimensioning');
  if (autoDimensioningStatus === 'supported') return null;
  return (
    <Group gap={6} wrap="nowrap" align="flex-start">
      {autoDimensioningStatus === 'measuring' ? (
        <Loader size="sm" />
      ) : (
        <EvolveIcon icon="Info" size="sm" color="gray" />
      )}
      <Text fz="xs" c="dimmed">
        {statusMessages[autoDimensioningStatus]}
      </Text>
    </Group>
  );
};

/**
 * In-viewer control for the automatic conduit-rack dimensions. The toggle lives in the `dimensioning`
 * store rather than component state, so dimensions stay drawn while this panel is closed.
 */
export const AutoDimensioningPanelSection = () => {
  const { dimensionsVisible } = useAutodeskExtensionStoreSnapshot('dimensioning');
  const { setRackDimensionsVisible } = useAutodeskExtensionStoreActions('dimensioning');

  return (
    <Stack gap="xs" mt="sm">
      <Switch
        label="Show Conduit Rack Dimensions"
        description="Automatically show conduit dimensions for the selected spools"
        labelPosition="left"
        checked={dimensionsVisible}
        onChange={(event) => setRackDimensionsVisible(event.currentTarget.checked)}
      />
      <AutoDimensionStatusSummary />
    </Stack>
  );
};
